import React from 'react';
import Cards from 'components/Card';
import { getProfessionInfo } from 'actions/profession';
import qs from 'qs';
import { useTranslate } from 'hooks/useTranslate';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MainLayout from 'layouts/MainLayout';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Helmet from 'components/Helmet';
import styled from '@emotion/styled';
import RenderPageSchema from 'components/RenderPageSchema';
import Breadcrumbs from 'components/BreadCrumbs';
import { getVacancyInfo } from 'actions/vacancy';
import awaitDelay from 'helpers/awaitDelay';
import getCurrentId from 'helpers/getCurrentId';
import renderHtml from 'helpers/renderHtml';
import isCyrillic from 'helpers/isCyrillic';
import { buildEntityUrl, getVacancySlug } from 'utils/seo';
import LinearProgress from 'components/LinearProgress';
import Vacancies from 'pages/Profession/components/Vacancies';
import useDataListFactory from 'services/dataList';
import vacanciesEndpoint from 'endPoints/vacancies';
import MotivationMessage from './components/MotivationMessage';
import SubmitForm from './components/SubmitForm';
import Bg1 from './assets/bg1.svg?react';
import Bg2 from './assets/bg2.svg?react';
import Approved from './assets/illustration-approved.svg?react';

const useDataList = (props, options) => useDataListFactory(props, options);

const ContentBackground = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.grey[600],
  position: 'relative'
}));

const Content = styled('section')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  minHeight: 'calc(100vh - 235px)',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const Card = styled('div')(({ theme, background, sx = {}, sm = {} }) => ({
  backgroundColor: background || theme.palette.white.main,
  padding: '24px 32px',
  borderRadius: 24,
  marginTop: 24,
  position: 'relative',
  ...sx,
  [theme.breakpoints.down('sm')]: {
    padding: 24,
    ...sm
  }
}));

const CardLeftSidebar = styled('div')(({ theme }) => ({
  width: '70%',
  [theme.breakpoints.down('md')]: {
    width: '100%'
  }
}));

const Divider = styled('div')(({ theme }) => ({
  width: 1,
  backgroundColor: theme.palette.grey[200],
  margin: 0,
  [theme.breakpoints.down('sm')]: {
    margin: 0,
    width: '100%',
    height: 2
  }
}));

const Title = styled('h1')(({ theme }) => ({
  fontSize: 28,
  fontWeight: 500,
  color: theme.palette.grey[600],
  margin: 0,
  marginBottom: 27,
  fontFamily: 'Unbounded',
  [theme.breakpoints.down('md')]: {
    fontSize: 24,
    marginBottom: 16
  }
}));

const Flex = styled('div')(({ justify, mb, mt, mts, theme }) => ({
  display: 'flex',
  justifyContent: justify || 'flex-start',
  marginBottom: mb || 16,
  marginTop: mt || 0,
  gap: 20,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    marginTop: mts || mt || 0,
    gap: 0
  }
}));

const FlexItem = styled('div')(() => ({
  flex: 1
}));

const Headline = styled(Typography)(({ theme, mt, mb, fz, ff, mbs, mw, mbsm, color }) => ({
  fontSize: fz || 18,
  fontWeight: 500,
  color: color || theme.palette.grey[600],
  marginTop: mt || 0,
  marginBottom: mb || 0,
  fontFamily: ff || 'Rubik-Regular',
  lineHeight: 'normal',
  maxWidth: mw || '100%',
  [theme.breakpoints.down('md')]: {
    marginBottom: mbs || mb || 0
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: mbsm || mb || 0
  }
}));

const Text = styled(Typography)(({ mb, fz, theme }) => ({
  fontSize: fz || 16,
  color: theme.palette.grey[600],
  lineHeight: '150%',
  fontWeight: 400,
  marginBottom: mb
}));

const FlexCard = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: 32,
  [theme.breakpoints.down('sm')]: {
    gap: 24,
    flexDirection: 'column-reverse'
  }
}));

const ButtonStyled = styled(Button)(({ theme }) => ({
  // Mobile sticky placement only. Bg / color / padding / radius / font
  // come from the theme's contained-primary variant — caller must pass
  // `variant="contained" color="primary"`. Earlier this only painted bg +
  // hover, but the caller didn't pass variant, so the button rendered as
  // a `variant="text"` (no padding) → "Відгукнутися" was visibly shorter
  // than every other CTA in the app.
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: 10,
    left: 10,
    width: 'calc(100% - 20px)',
    zIndex: 100
  }
}));

const Bg1Styled = styled(Bg1)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const Bg2Styled = styled(Bg2)(({ theme }) => ({
  position: 'absolute',
  left: 200,
  top: 980,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const Tag = styled('span')(({ sx = {}, theme }) => ({
  display: 'inline-block',
  border: `1px solid ${theme.palette.grey[400]}`,
  padding: '8px 16px',
  borderRadius: 6,
  fontSize: 14,
  color: theme.palette.grey[600],
  marginRight: 8,
  marginBottom: 8,
  ...sx
}));

const ApprovedStyled = styled(Approved)(() => ({
  position: 'absolute',
  right: 0,
  top: 0
}));

const ContactsBlock = styled('div')(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  marginTop: 24,
  paddingTop: 24,
  [theme.breakpoints.down('sm')]: {
    marginTop: 16,
    paddingTop: 12
  }
}));

const BlackLink = styled('a')(({ theme }) => ({
  color: theme.palette.grey[600],
  cursor: 'pointer',
  textDecoration: 'none'
}));

const BlueLink = styled('a')(({ theme }) => ({
  color: theme.palette.tertiary.main,
  cursor: 'pointer'
}));

const CompanyLogo = styled('img')(({ mb, theme }) => ({
  maxWidth: 120,
  maxHeight: 100,
  marginBottom: mb || 24,
  objectFit: 'contain',
  [theme.breakpoints.down('sm')]: {
    marginBottom: 16
  }
}));

const CardRightSidebar = styled('div')(({ theme }) => ({
  width: '30%',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginTop: 0
  }
}));

const StickySidebar = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 24,
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    top: 0
  }
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  textAlign: 'center',
  color: theme.palette.grey[200]
}));

// Scraped vacancy descriptions (dou.ua, jooble, …) often begin with a
// redundant header — a date, an <h2> repeating the job title, and a location
// line — all already shown elsewhere on the page. Drop that lead-in when the
// leading <h2> matches the vacancy title.
const stripScrapedHeader = (html, title) => {
  if (!html || !title) return html;
  const h2 = html.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i);
  if (!h2) return html;
  const normalize = (s) =>
    s.replace(/<[^>]*>/g, '').replace(/&#160;|&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  if (normalize(h2[1]) !== normalize(title)) return html;
  const afterTitle = html.slice(h2.index + h2[0].length);
  // Also drop the location paragraph that immediately follows the title.
  return afterTitle.replace(/^\s*<p[^>]*>[\s\S]*?<\/p>/i, '').trim();
};

const Vacancy = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = React.useState(false);
  const [vacancy, setVacancy] = React.useState({});
  const [applyShow, setApplyShow] = React.useState(false);
  const [currentId] = React.useState(getCurrentId(location));
  const [professions, setProfessions] = React.useState([]);
  const applyRef = React.useRef();

  const t = useTranslate('Vacancy');
  const tHelmet = useTranslate('HELMET');



  React.useEffect(() => {
    if (!vacancy) return;

    const professionsId = vacancy.professionIds || [];

    if (!professionsId || !professionsId.length) return;

    const fetchProfessions = async () => {
      const fetchedProfessions = [];
      // Limit to first 4 professions to avoid too many API calls and UI clutter
      const limitedIds = professionsId.slice(0, 4);
      for (const id of limitedIds) {
        const query = qs.stringify({
          fields: ['_id', 'tags', 'photoLinks', 'name', 'descriptionShort']
        });
        const profession = await dispatch(getProfessionInfo(id, `?${query}`));
        // Only add valid profession objects (must have _id and name)
        if (profession && profession._id && profession.name) {
          fetchedProfessions.push(profession);
        }
      }
      setProfessions(fetchedProfessions);
    };

    fetchProfessions();
  }, [vacancy, dispatch]);

  const {
    data: vacancies,
    actions: { onFilterChange }
  } = useDataList(
    {
      ...vacanciesEndpoint,
      autoLoad: false
    },
    {
      perPage: 8
    }
  );

  const toggleApplyShow = React.useCallback(async () => {
    if (vacancy?.source === 'original') {
      setApplyShow(true);
      await awaitDelay(250);
      applyRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    if (vacancy.link) {
      window.open(vacancy.link, '_blank');
    }
  }, [applyRef, vacancy]);

  const breadcrumbs = React.useMemo(() => {
    const color = theme.palette.white.main;
    return [
      { label: 'Home', link: '/', color },
      { label: 'Vacancies', link: '/vacancies', color },
      { label: vacancy?.title || '', raw: true, color }
    ];
  }, [theme.palette.white.main, vacancy?.title]);

  const canonicalUrl = React.useMemo(() => {
    if (!vacancy || !vacancy._id) return undefined;
    return buildEntityUrl('vacancy', getVacancySlug(vacancy), vacancy._id);
  }, [vacancy]);

  React.useEffect(() => {
    // getCurrentId returns null when the URL has no valid 24-hex ObjectId.
    // Skip the fetch instead of dispatching with null — the API would 404 and
    // surface as "Cannot get vacancy" in Sentry. The Vacancies list upstream
    // already filters out items without _id, so reaching here means the user
    // typed/landed on a malformed URL directly.
    if (!currentId) return;
    const fetchData = async () => {
      setLoading(true);
      const result = await dispatch(getVacancyInfo(currentId));
      setVacancy(result);
      setLoading(false);
      onFilterChange({ companies: result?.company?._id }, true);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentId]);

  const renderContent = React.useCallback((content) => {
    if (!content) return null;
    return content.split('\n').map((item, index) => {
      return (
        <Text key={index} mb={!item ? 10 : 0}>
          {renderHtml(item)}
        </Text>
      );
    });
  }, []);

  if (loading) {
    return <LinearProgress />;
  }

  // Meta title: the localized "Vacancy" word leads (prefix languages) or
  // trails (suffix languages, e.g. English) the job title, optionally
  // followed by the localized "at <company>" clause. vacancyAtCompany is a
  // template with a {{company}} placeholder so each language controls word
  // order (preposition vs. postposition).
  const companyName = vacancy.company?.name || '';
  let vacancyMetaTitle = tHelmet('vacancyOgTitle');
  if (vacancy.title) {
    const prefix = tHelmet('vacancyTitlePrefix');
    const labelled = prefix
      ? `${prefix} ${vacancy.title}`
      : `${vacancy.title} ${tHelmet('vacancyTitleSuffix')}`.trim();
    vacancyMetaTitle = [
      labelled,
      companyName && tHelmet('vacancyAtCompany', { company: companyName })
    ].filter(Boolean).join(' ');
  }

  const description = stripScrapedHeader(vacancy.description, vacancy.title);

  return (
    <>
      <Helmet
        meta={{
          title: vacancyMetaTitle,
          description: vacancy.description || tHelmet('vacancyDescription'),
          ogTitle: vacancyMetaTitle,
          ogDescription: vacancy.description || tHelmet('vacancyOgDescription')
        }}
        canonicalUrl={canonicalUrl}
      />
      <MainLayout>
        <Header backgroundColor={theme.palette.grey[600]} />
        <ContentBackground>
          <Bg1Styled />
          <Bg2Styled />

          <Content>
            <Breadcrumbs breadcrumbs={breadcrumbs} mobileShowParent />

            <Card>
              <FlexCard>
                <CardLeftSidebar>
                  <Title>{vacancy.title}</Title>

                  {description ? (
                    <Headline mb={8} mbs={12}>
                      {t('Description')}
                    </Headline>
                  ) : null}

                  {renderContent(description)}

                  <RenderPageSchema
                    hideTitle={true}
                    sx={{ fontSize: 16 }}
                    schema={{
                      layout: vacancy?.descriptionRichtext
                    }}
                  />

                  {vacancy?.requiredSkills && vacancy?.requiredSkills.length ? (
                    <Headline mb={12}>{t('Skills')}</Headline>
                  ) : null}

                  {(vacancy?.requiredSkills || []).map((category) => (
                    <Tag
                      key={category?.name}
                      sx={{ borderColor: theme.palette.primary.main, borderRadius: 36 }}>
                      <Text>{category?.name}</Text>
                    </Tag>
                  ))}

                  <Headline mt={16}>
                    {t('Published')} {dayjs(vacancy.updated).format('DD.MM.YYYY')}
                  </Headline>
                </CardLeftSidebar>

                <Divider />

                <CardRightSidebar>
                  <StickySidebar>
                    {vacancy?.company?.logo ? (
                      <CompanyLogo src={vacancy?.company?.logo} alt={vacancy?.company?.name} />
                    ) : null}

                    <Text>{t('Level')}</Text>
                    <Headline mb={24}>
                      {vacancy.noexperience ? t('noexperience') : t('experience')}
                    </Headline>

                    {vacancy?.type ? (
                      <>
                        <Text>{t('Type')}</Text>
                        <Headline mb={24} mw={200}>
                          {isCyrillic(vacancy.type) ? vacancy.type : t(vacancy.type)}
                        </Headline>
                      </>
                    ) : null}

                    <Text>{t('Salary')}</Text>
                    <Headline mb={24}>
                      {vacancy.salary
                        ? `${vacancy.salary} ${t('UAH')}`
                        : t('SalaryNotSpecified', { defaultValue: '-' })}
                    </Headline>

                    {vacancy?.location ? <Text>{t('Location')}</Text> : null}

                    <Headline mb={24}>{vacancy.location}</Headline>

                    {vacancy?.contactPerson ? (
                      <ContactsBlock>
                        <Text fz={16} mb={6}>
                          {t('Contact')}
                        </Text>
                        <Headline mb={6}>
                          <BlackLink href={`tel:${vacancy?.contactPerson?.phone}`}>
                            {vacancy?.contactPerson?.phone}
                          </BlackLink>
                        </Headline>
                        <Headline mb={6}>
                          <BlackLink href={`mailto:${vacancy?.contactPerson?.email}`}>
                            {vacancy?.contactPerson?.email}
                          </BlackLink>
                        </Headline>
                        <Text fz={16}>{vacancy?.contactPerson?.name}</Text>
                      </ContactsBlock>
                    ) : null}

                    <ButtonStyled
                      variant="contained"
                      color="primary"
                      onClick={toggleApplyShow}
                      sx={{ width: '100%', mt: 4 }}>
                      {t('Apply')}
                    </ButtonStyled>

                    {vacancy?.source === 'original' ? <ApprovedStyled /> : null}
                  </StickySidebar>
                </CardRightSidebar>
              </FlexCard>
            </Card>

            <Card>
              <Flex justify="space-between" mb={5}>
                {vacancy?.company?.logo ? (
                  <CompanyLogo src={vacancy?.company?.logo} alt={vacancy?.company?.name} mb={5} />
                ) : null}

                {vacancy?.company?.socialMedia?.web ? (
                  <Headline fz={16} mbsm={12}>
                    {t('CompanySite')}:{' '}
                    <BlueLink
                      href={vacancy?.company?.socialMedia?.web}
                      target="_blank"
                      rel="noopener noreferrer">
                      {vacancy?.company?.socialMedia?.web}
                    </BlueLink>
                  </Headline>
                ) : null}
              </Flex>

              <Headline mb={5}>{t('CompanyDescription')}</Headline>

              {vacancy?.company?.aboutIntro || vacancy?.company?.aboutFull ? (
                <>
                  <RenderPageSchema
                    hideTitle={true}
                    sx={{ fontSize: 16 }}
                    schema={{
                      layout: [vacancy?.company?.aboutIntro]
                    }}
                  />

                  <RenderPageSchema
                    hideTitle={true}
                    sx={{ fontSize: 16 }}
                    schema={{
                      layout: [vacancy?.company?.aboutFull]
                    }}
                  />
                </>
              ) : (
                <>
                  <Text>{vacancy?.company?.name || ''}</Text>
                  <Text>{vacancy?.company?.description || ''}</Text>
                </>
              )}
            </Card>

            <div ref={applyRef}>
              {applyShow ? (
                <Fade in={true}>
                  <Card>
                    <Headline ff={'Unbounded'} fz={24} mb={24}>
                      {t('CV')}
                    </Headline>
                    <SubmitForm vacancy={vacancy}>
                      <HelperText>{t('AgreeToTerms')}</HelperText>
                    </SubmitForm>
                  </Card>
                </Fade>
              ) : null}
            </div>

            <Flex justify="space-between">
              <FlexItem>
                <MotivationMessage fullWidth={true} brandColor={vacancy?.company?.brandColor} />
              </FlexItem>
            </Flex>

            <Vacancies
              vacanciesList={vacancies}
              customHeadline={t('SimilarVacancies')}
              darkTheme={true}
              pagination={false}
            />

            {professions.length > 0 ? (
              <div style={{ marginTop: 40 }}>
                <Headline variant="h2" mb={24} color={theme.palette.white.main}>
                  {t('RelatedProfessions')}
                </Headline>
                <Cards list={professions} columns={4} />
              </div>
            ) : null}
          </Content>
        </ContentBackground>
        <Footer backgroundColor={theme.palette.grey[600]} />
      </MainLayout>
    </>
  );
};

export default Vacancy;
